import React, { useMemo } from 'react';
import { Group, ActionIcon, Menu, Box, Tree } from '@mantine/core';
import { ContextMenuContent, useContextMenu } from 'mantine-contextmenu';
import {
  IconFile,
  IconDots,
  IconPencil,
  IconTrash,
  IconDownload,
  IconHistory,
  IconRocket,
  IconArrowBounce,
  IconFolder,
  IconFolderOpen,
  IconChevronDown,
  IconMusic,
  IconExternalLink,
  IconPlayerPlay,
  IconPlayerPause,
  IconFolderPlus,
} from '@tabler/icons-react';
import { DBObjects } from '@/store/store_types';
import { isAudioFile } from '@/utils/file_utils';
import { PlayIcon } from '@/modules/audio_player/audio_player_module';
import { useModals } from '@/modules/music_sniper/components/modals/modals_provider';
import { useCurrentUser } from '@/modules/music_sniper/hooks/useCurrentUser';
import { useModule } from '@/hooks/useModule';
import { useAction } from '@/store/kysely_module';

type FileInfo = DBObjects['File'] & { num_revisions: number; duration: string | null };

type Props = {
  fileInfos: FileInfo[];
  onItemClick: (fileInfo: DBObjects['File']) => void;
  refresh: () => void;
  makeLauncherFile?: (relativePath: string) => void;
  makeBounceFile?: (relativePath: string) => void;
  projectId?: string;
  workspaceId?: string;
};

export const FileTableWithActions: React.FC<Props> = ({
  fileInfos,
  onItemClick,
  refresh,
  makeLauncherFile,
  makeBounceFile,
  projectId,
  workspaceId,
}) => {
  const modals = useModals();
  const currentUser = useCurrentUser();
  const msFilesModule = useModule('MSFiles');
  const localDesktopFilesModule = useModule('LocalDesktopFiles');
  const audioPlayerModule = useModule('AudioPlayer');
  const { showContextMenu } = useContextMenu();

  const updateFileName = useAction('updateFileName');
  const deleteFile = useAction('deleteFile');

  const playbackStatus = audioPlayerModule?.useAudioPlaybackStatus();

  const sortedFiles = [...fileInfos].sort((a, b) =>
    a.created_at > b.created_at ? -1 : 1
  );

  const treeData = useMemo(() => {
    const root: any = { children: [] };
    const nodeMap = new Map();

    sortedFiles.forEach((fileInfo) => {
      const parts = fileInfo.relative_path.split('/');
      let currentNode = root;
      let currentPath = '';

      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        currentPath = currentPath ? `${currentPath}/${part}` : part;

        if (i === parts.length - 1) {
          currentNode.children.push({
            label: fileInfo.name,
            value: fileInfo.relative_path,
            fileInfo: fileInfo,
            isFile: true,
          });
        } else {
          let childNode = nodeMap.get(currentPath);
          if (!childNode) {
            childNode = {
              label: part,
              value: currentPath,
              children: [],
              isFile: false,
            };
            nodeMap.set(currentPath, childNode);
            currentNode.children.push(childNode);
          }
          currentNode = childNode;
        }
      }
    });

    return root.children;
  }, [sortedFiles]);

  const handleRename = (fileInfo: FileInfo) => {
    const lastPeriodIndex = fileInfo.name.lastIndexOf('.');
    const fileName = fileInfo.name.substring(0, lastPeriodIndex);
    const suffix = fileInfo.name.substring(lastPeriodIndex);

    modals.openModal('TextInputModal', {
      title: 'Rename file',
      inputName: 'File name',
      initialValue: fileName,
      suffix,
      onSubmit: async (fileName) => {
        const fullName = `${fileName}${suffix}`;
        await updateFileName(
          { file_id: fileInfo.id, new_name: fullName },
          { workspace_id: fileInfo.workspace_id }
        );
        refresh();
      },
    });
  };

  const handleDelete = (fileInfo: FileInfo) => {
    modals.openModal('ConfirmationModal', {
      title: 'Delete this file?',
      message: 'This action cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        await deleteFile(
          { file_id: fileInfo.id },
          { workspace_id: fileInfo.workspace_id }
        );
        refresh();
      },
    });
  };

  const handleDownload = async (fileInfo: FileInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    const fileSrc = await msFilesModule.client().getFileSrc(fileInfo);
    const link = document.createElement('a');
    link.download = fileInfo.name;
    link.href = fileSrc;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getLocalFolderPath = async (relativePath: string): Promise<string | undefined> => {
    if (!localDesktopFilesModule || !workspaceId || !projectId) {
      return undefined;
    }

    return localDesktopFilesModule.getLocalFolderPath({
      relativePath,
      workspace_id: workspaceId,
      projectId: projectId,
    });
  };

  const openFolderWithDefaultProgram = async (relativePath: string) => {
    if (!localDesktopFilesModule || !workspaceId) return;

    const folderPath = await getLocalFolderPath(relativePath);
    if (!folderPath) return;

    const folderName = relativePath.split('/').pop() || relativePath;
    await localDesktopFilesModule.openWithFileHelpers.openFileWith(folderName, folderPath);
  };

  const openFolderWithChosenProgram = async (relativePath: string) => {
    if (!localDesktopFilesModule || !workspaceId) return;

    const folderName = relativePath.split('/').pop() || relativePath;
    const execPath = await localDesktopFilesModule.openWithFileHelpers.chooseExecutable(folderName);
    if (!execPath) return;

    const folderPath = await getLocalFolderPath(relativePath);
    if (!folderPath) return;

    await localDesktopFilesModule.openWithFileHelpers.openFileWith(folderName, folderPath);
  };

  const openFolderInExplorer = async (relativePath: string) => {
    if (!localDesktopFilesModule) return;

    const folderPath = await getLocalFolderPath(relativePath);
    if (!folderPath) return;

    await localDesktopFilesModule.openWithFileHelpers.openFolderInExplorer(folderPath);
  };

  const getLocalFilePath = async (fileInfo: DBObjects['File']): Promise<string | undefined> => {
    if (!localDesktopFilesModule || !projectId) {
      return undefined;
    }

    return localDesktopFilesModule.getLocalFilePath({
      fileInfo,
      projectId,
    });
  };

  const openWithDefaultProgram = async (fileInfo: DBObjects['File']) => {
    if (!localDesktopFilesModule) return;

    const filePath = await getLocalFilePath(fileInfo);
    if (!filePath) return;

    await localDesktopFilesModule.openWithFileHelpers.openFileWith(fileInfo.name, filePath);
  };

  const openWithChosenProgram = async (fileInfo: DBObjects['File']) => {
    if (!localDesktopFilesModule) return;

    const execPath = await localDesktopFilesModule.openWithFileHelpers.chooseExecutable(fileInfo.name);
    if (!execPath) return;

    const filePath = await getLocalFilePath(fileInfo);
    if (!filePath) return;

    await localDesktopFilesModule.openWithFileHelpers.openFileWith(fileInfo.name, filePath);
  };

  const getFolderContextMenu = (folderPath: string): ContextMenuContent => {
    const menuItems: ContextMenuContent = [];

    if (localDesktopFilesModule) {
      const folderName = folderPath.split('/').pop() || '';

      menuItems.push({
        key: 'open-with-default',
        icon: <localDesktopFilesModule.openWithFileHelpers.IconForFile ext={folderName} />,
        title: 'Open with default',
        onClick: () => openFolderWithDefaultProgram(folderPath),
      });

      menuItems.push({
        key: 'open-with-choose',
        icon: <IconFolder size={16} />,
        title: 'Open with...',
        onClick: () => openFolderWithChosenProgram(folderPath),
      });

      menuItems.push({
        key: 'open-explorer',
        icon: <IconFolderOpen size={16} />,
        title: 'Show in File Explorer',
        onClick: () => openFolderInExplorer(folderPath),
      });
    }

    if (makeLauncherFile) {
      menuItems.push({
        key: 'make-launcher',
        icon: <IconRocket size={16} />,
        title: 'Make launcher',
        onClick: () => {
          makeLauncherFile(folderPath);
        },
      });
    }

    return menuItems;
  };

  const getFileContextMenu = (fileInfo: FileInfo): ContextMenuContent => {
    const menuItems: ContextMenuContent = [];

    if (isAudioFile(fileInfo.name) && audioPlayerModule) {
      const isCurrentFile = playbackStatus?.fileInfo.id === fileInfo.id;
      const isPlaying = isCurrentFile && playbackStatus?.isPlaying;

      menuItems.push({
        key: 'play',
        icon: isPlaying ? <IconPlayerPause size={16} /> : <IconPlayerPlay size={16} />,
        title: isPlaying ? 'Pause' : 'Play',
        onClick: () => {
          if (isPlaying) {
            audioPlayerModule.playPauseToggle();
          } else {
            audioPlayerModule.playFile(fileInfo);
          }
        },
      });
    }

    menuItems.push({
      key: 'rename',
      icon: <IconPencil size={16} />,
      title: 'Edit name',
      onClick: () => handleRename(fileInfo),
    });

    menuItems.push({
      key: 'download',
      icon: <IconDownload size={16} />,
      title: 'Download',
      onClick: async () => {
        const fileSrc = await msFilesModule.client().getFileSrc(fileInfo);
        const link = document.createElement('a');
        link.download = fileInfo.name;
        link.href = fileSrc;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
    });

    if (localDesktopFilesModule) {
      menuItems.push({
        key: 'open-with-default',
        icon: <localDesktopFilesModule.openWithFileHelpers.IconForFile ext={fileInfo.name} />,
        title: 'Open with default',
        onClick: () => openWithDefaultProgram(fileInfo),
      });

      menuItems.push({
        key: 'open-with-choose',
        icon: <IconFile size={16} />,
        title: 'Open with...',
        onClick: () => openWithChosenProgram(fileInfo),
      });
    }

    if (makeLauncherFile) {
      menuItems.push({
        key: 'make-launcher',
        icon: <IconRocket size={16} />,
        title: 'Make launcher',
        onClick: () => {
          makeLauncherFile(fileInfo.relative_path);
        },
      });
    }

    if (makeBounceFile && isAudioFile(fileInfo.name)) {
      menuItems.push({
        key: 'make-bounce',
        icon: <IconArrowBounce size={16} />,
        title: 'Make bounce',
        onClick: () => {
          makeBounceFile(fileInfo.relative_path);
        },
      });
    }

    if (fileInfo.created_by === currentUser?.id) {
      menuItems.push({
        key: 'delete',
        icon: <IconTrash size={16} color="red" />,
        title: 'Delete',
        onClick: () => handleDelete(fileInfo),
      });
    }

    return menuItems;
  };

  return (
    <Box
      style={{
        fontFamily: 'monospace',
        fontSize: '14px',
      }}
    >
      <Tree
        data={treeData}
        expandOnClick
        levelOffset={24}
        renderNode={({ node, expanded, hasChildren, elementProps }) => {
          const isFile = (node as any).isFile;
          const fileInfo = (node as any).fileInfo as FileInfo | undefined;

          return (
            <div
              {...elementProps}
              style={{
                ...elementProps.style,
                cursor: 'pointer',
              }}
              onClick={(e) => {
                if (elementProps.onClick) {
                  elementProps.onClick(e);
                }
                if (isFile && fileInfo) {
                  onItemClick(fileInfo);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                if (isFile && fileInfo) {
                  showContextMenu(getFileContextMenu(fileInfo))(e);
                } else if (!isFile) {
                  const folderPath = node.value as string;
                  showContextMenu(getFolderContextMenu(folderPath))(e);
                }
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {hasChildren && (
                  <IconChevronDown
                    size={16}
                    style={{
                      transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                      transition: 'transform 0.2s ease',
                      color: '#666',
                    }}
                  />
                )}

                {isFile && fileInfo && isAudioFile(fileInfo.name) && (
                  <PlayIcon
                    fileInfo={fileInfo}
                    style={{
                      position: 'absolute',
                      left: 30 + (elementProps.style?.left ? Number(elementProps.style.left) : 0),
                    }}
                    size={12}
                  />
                )}

                {!hasChildren && <Box style={{ width: 16 }} />}

                {isFile ? (
                  fileInfo && isAudioFile(fileInfo.name) ? (
                    <IconMusic size={16} style={{ color: '#1971c2' }} />
                  ) : (
                    <IconFile size={16} style={{ color: '#868e96' }} />
                  )
                ) : expanded ? (
                  <IconFolderOpen size={16} style={{ color: '#fab005' }} />
                ) : (
                  <IconFolder size={16} style={{ color: '#fab005' }} />
                )}

                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                >
                  {typeof node.label === 'string' && node.label.length > 50 ? node.label.substring(0, 50) + '...' : node.label}
                </span>

                {isFile && fileInfo ? (
                  <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                    {fileInfo.num_revisions > 1 && (
                      <Group gap={2} style={{ fontSize: '12px', color: '#868e96' }}>
                        <IconHistory size={12} />
                        <span>{fileInfo.num_revisions}</span>
                      </Group>
                    )}
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(fileInfo, e);
                      }}
                      title="Download"
                    >
                      <IconDownload size={14} />
                    </ActionIcon>
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" size="sm">
                          <IconDots size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() => handleRename(fileInfo)}
                        >
                          Rename
                        </Menu.Item>
                        {makeLauncherFile && (
                          <Menu.Item
                            leftSection={<IconRocket size={14} />}
                            onClick={() => makeLauncherFile(fileInfo.relative_path)}
                          >
                            Assign as Launcher File
                          </Menu.Item>
                        )}
                        {makeBounceFile && isAudioFile(fileInfo.name) && (
                          <Menu.Item
                            leftSection={<IconArrowBounce size={14} />}
                            onClick={() => makeBounceFile(fileInfo.relative_path)}
                          >
                            Assign as Bounce File
                          </Menu.Item>
                        )}
                        {fileInfo.created_by === currentUser?.id && (
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => handleDelete(fileInfo)}
                          >
                            Delete
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                ) : !isFile && (makeLauncherFile || localDesktopFilesModule) && (
                  <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                    <Menu position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" size="sm">
                          <IconDots size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {localDesktopFilesModule && (
                          <>
                            <Menu.Item
                              leftSection={
                                localDesktopFilesModule.openWithFileHelpers ? (
                                  <localDesktopFilesModule.openWithFileHelpers.IconForFile
                                    ext={node.label as string}
                                  />
                                ) : (
                                  <IconExternalLink size={14} />
                                )
                              }
                              onClick={() => openFolderWithDefaultProgram(node.value as string)}
                            >
                              Open with default
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconFolder size={14} />}
                              onClick={() => openFolderWithChosenProgram(node.value as string)}
                            >
                              Open with...
                            </Menu.Item>
                            {makeLauncherFile && <Menu.Divider />}
                          </>
                        )}
                        {makeLauncherFile && (
                          <Menu.Item
                            leftSection={<IconRocket size={14} />}
                            onClick={() => makeLauncherFile(node.value as string)}
                          >
                            Assign as Launcher File
                          </Menu.Item>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                )}
              </div>
            </div>
          );
        }}
      />
    </Box>
  );
};
